import { useState } from 'react';
import { NextPage } from 'next';
import { NextSeo, NextSeoProps } from 'next-seo';
import Head from 'next/head';

import { useRouter } from 'next/router';

import { structuredData } from '@temp/core/SEO/Reviews/structuredData';

import { Loader } from '@components/atoms/Loader';
import { NotFound } from '@pages/NotFound';

import { localeToSaleorQuery } from '@utils/regions';
import { maybe } from '@utils/tsUtils';

import { useReviewsQuery } from '@sdk/gqlTypes/generatedHooks';

import { REVIEWS_PER_PAGE } from '../../core/config';
import Page from './Page';

export interface ViewProps {
  seo: NextSeoProps | null;
}

const View: NextPage<ViewProps> = ({ seo }) => {
  const [hasImages, setHasImages] = useState(true);
  const { locale } = useRouter();
  const { data, loading, fetchMore, networkStatus } = useReviewsQuery({
    variables: {
      hasImages,
      ...localeToSaleorQuery(locale),
      pageSize: REVIEWS_PER_PAGE,
    },
  });
  const isRefetching = networkStatus === 3;

  const onLoadMore = () => {
    fetchMore({
      variables: {
        after: data?.reviews?.pageInfo.endCursor,
      },
    });
  };

  if (loading) {
    return <Loader />;
  }
  if (data && data.reviews === null) {
    return <NotFound />;
  }

  const reviews = data.reviews.edges.map(({ node }) => node);

  return (
    <>
      <NextSeo {...seo} />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: structuredData(reviews) }}
          key="reviews-jsonld"
        />
      </Head>
      <Page
        hasImages={hasImages}
        setHasImages={setHasImages}
        loading={isRefetching}
        canLoadMore={maybe(() => data.reviews.pageInfo.hasNextPage, false)}
        reviews={reviews}
        onLoadMore={onLoadMore}
      />
    </>
  );
};

export default View;
